import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Flex, IconButton, Tooltip } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';
import { CheckIcon, DeleteIcon, EditIcon, NotInterestedIcon } from 'shared/icons';
import { SimpleResume } from '../types';
import { useSimpleResumeManager } from '../simple-resume-manager.provider';
import { SimpleResumeConfirmDialog } from '../simple-resume-confirm-dialog';

import './simple-resume-actions-menu.scss';

type SimpleResumeMenuAction = 'ACTIVATE' | 'DEACTIVATE' | 'DELETE' | 'EMPTY';

const SimpleResumeActionsMenuView = ({ resume }: { resume: SimpleResume }) => {
   const navigate = useNavigate();
   const location = useLocation();
   const simpleResumeManager = useSimpleResumeManager();
   const [open, setOpen] = React.useState(false);
   const [action, setAction] = React.useState<SimpleResumeMenuAction>('EMPTY');

   return (
      <Flex className="simple-resume-actions-menu" gap="1" align="center" justify="center">
         {resume.is_active === 1 ? (
            <Tooltip content="Deactivate" side="left">
               <IconButton
                  color="green"
                  className="action-trigger"
                  onClick={() => {
                     setOpen(true);
                     setAction('DEACTIVATE');
                  }}
               >
                  <CheckIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         ) : (
            <Tooltip content="Activate" side="left">
               <IconButton
                  color="red"
                  className="action-trigger"
                  onClick={() => {
                     setOpen(true);
                     setAction('ACTIVATE');
                  }}
               >
                  <NotInterestedIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         )}

         <Tooltip content="Edit" side="top">
            <IconButton
               color="blue"
               className="action-trigger"
               highContrast
               onClick={() => {
                  navigate(`/admin/simple-resumes/${resume.id}/edit${location.search}`);
               }}
            >
               <EditIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Delete" side="top">
            <IconButton
               color="red"
               className="action-trigger"
               onClick={() => {
                  setOpen(true);
                  setAction('DELETE');
               }}
            >
               <DeleteIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
               if (!nextOpen) {
                  setAction('EMPTY');
               }
               setOpen(nextOpen);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(event) => event.preventDefault()}
            >
               {
                  {
                     ACTIVATE: (
                        <SimpleResumeConfirmDialog
                           title="Activate Resume"
                           message="Do you want to Activate this Resume?"
                           loading={simpleResumeManager.activateResumeState.status === 'pending'}
                           onConfirm={() => {
                              simpleResumeManager.activateResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     DEACTIVATE: (
                        <SimpleResumeConfirmDialog
                           title="Deactivate Resume"
                           message="Do you want to Deactivate this Resume?"
                           loading={simpleResumeManager.deactivateResumeState.status === 'pending'}
                           onConfirm={() => {
                              simpleResumeManager.deactivateResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     DELETE: (
                        <SimpleResumeConfirmDialog
                           title="Delete Resume"
                           message="Do you want to Delete this Resume?"
                           loading={simpleResumeManager.deleteResumeState.status === 'pending'}
                           onConfirm={() => {
                              simpleResumeManager.deleteResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const SimpleResumeActionsMenu = observer(SimpleResumeActionsMenuView);
